import { Component, Input, OnChanges } from '@angular/core';
import { AgentKey, ExperimentProduct } from '../agentic-ecommerce.types';
import {
  formatComparisonFieldLabel,
  formatProductPrice,
  getAvailabilityText,
  getFoundComparisonFields,
  getMissingComparisonFields,
  getProductComparison,
} from '../agentic-ecommerce.utils';

@Component({
  selector: 'app-agentic-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.css'],
})
export class ProductCardComponent implements OnChanges {
  @Input() product: ExperimentProduct | null = null;
  @Input() visible: boolean = false;
  @Input() selectedAgentKey: AgentKey = '';
  @Input() selectedAgentLabel: string = '';
  @Input() isPdpPage: boolean = false;

  public imageFailed: boolean = false;

  ngOnChanges(): void {
    this.imageFailed = false;
  }

  public get productName(): string {
    return this.product?.name || 'Unnamed product';
  }

  public get productUrl(): string {
    return this.product?.url || '';
  }

  public get imageUrl(): string {
    return this.product?.imageUrl || '';
  }

  public get price(): string {
    return formatProductPrice(this.product);
  }

  public get ratingLabel(): string {
    if (!this.isPdpPage) {
      return '';
    }

    const rating = this.getOptionalProductValue('rating');

    if (!rating) {
      return '';
    }

    if (rating.includes('/5') || rating.toLowerCase().includes('out of')) {
      return rating;
    }

    return `${rating}/5`;
  }

  public get reviewCountLabel(): string {
    if (!this.isPdpPage) {
      return '';
    }

    const reviewCount = this.getOptionalProductValue('reviewCount');

    if (!reviewCount) {
      return '';
    }

    return Number(reviewCount) === 1 ? '1 review' : `${reviewCount} reviews`;
  }

  public get hasPdpProductMeta(): boolean {
    return !!this.ratingLabel || !!this.reviewCountLabel;
  }

  public get availabilityText(): string {
    return getAvailabilityText(this.product);
  }

  public get missingComparisonFields(): string[] {
    return getMissingComparisonFields(this.product, this.selectedAgentKey);
  }

  public get foundComparisonFields(): string[] {
    return getFoundComparisonFields(this.product, this.selectedAgentKey);
  }

  public get hasMissingComparisonFields(): boolean {
    return this.missingComparisonFields.length > 0;
  }

  public get hasFoundComparisonFields(): boolean {
    return this.foundComparisonFields.length > 0;
  }

  public get hasComparisonData(): boolean {
    return !!getProductComparison(this.product, this.selectedAgentKey);
  }

  public get comparisonThresholdMet(): boolean {
    return (
      getProductComparison(this.product, this.selectedAgentKey)?.thresholdMet ===
      true
    );
  }

  public get showNotSeenOverlay(): boolean {
    return !this.visible && !this.isPdpPage;
  }

  public formatComparisonField(field: string): string {
    return formatComparisonFieldLabel(field);
  }

  public onImageError(): void {
    this.imageFailed = true;
  }

  private getOptionalProductValue(key: 'rating' | 'reviewCount'): string {
    const value = this.product?.[key];

    if (value === null || value === undefined) {
      return '';
    }

    const formattedValue = String(value).trim();

    return formattedValue;
  }
}
